import { useTranslation } from 'react-i18next';
import React from 'react';
import { FlexColumn, PageTile } from '@make-software/cspr-design';
import { FailedToFetch } from '../page-data-table/table-error';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import {
  PaginationProps,
  TableSettings,
} from '../table-settings/table-settings';
import { TableViewId } from '../../redux/table-view';
import { NothingFound } from '../../scenes/NothingFoundScene';

const StyledFlexColumn = styled(FlexColumn)(() => ({
  width: '100%',
}));

const Grid = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'grid',
    gridTemplateColumns: [
      '1fr',
      'repeat(2, minmax(0, 1fr))',
      'repeat(3, minmax(0, 1fr))',
      'repeat(4, minmax(0, 1fr))',
    ],
    gap: [12, 24],
  })
);

const GridTile = styled(PageTile)(({ theme }) =>
  theme.withMedia({
    height: 'auto',
    padding: [12, 16],
  })
);

export interface TilesGridProps<Entity> {
  tableId: TableViewId;
  data: null | Entity[];
  renderPaginatedData: () => null | undefined | JSX.Element[];
  loading: boolean;
  error?: string | null;
  paginationProps: PaginationProps;
  filter?: JSX.Element;
  actions?: JSX.Element | JSX.Element[];
  hasNothingFound?: boolean;
  totalRowsLabel?: string;
}

export const TilesGrid = <Entity extends any>({
  tableId,
  data,
  renderPaginatedData,
  loading,
  error,
  paginationProps,
  filter,
  actions,
  hasNothingFound,
  totalRowsLabel,
}: TilesGridProps<Entity>) => {
  const { t } = useTranslation();
  const filterWithFullView = filter
    ? React.cloneElement(filter, { isFullView: true })
    : undefined;

  return (
    <StyledFlexColumn>
      <PageTile>
        <TableSettings
          {...paginationProps}
          hideRowsPerPage
          hidePaginationOnError={false}
          filter={filterWithFullView}
          actions={actions}
          tableId={tableId}
          totalRowsLabel={totalRowsLabel}
        />
      </PageTile>
      {error ? (
        <PageTile>
          <FailedToFetch />
        </PageTile>
      ) : hasNothingFound ? (
        <PageTile>
          <NothingFound message={t('No data found')} />
        </PageTile>
      ) : (
        <Grid>
          {loading || !data
            ? Array(paginationProps.perPage || 20)
                .fill(undefined)
                .map((_, index) => (
                  <GridTile key={index}>
                    <FlexColumn gap={24}>
                      <Skeleton height={200} />
                      <Skeleton height={20} width={'70%'} />
                      <Skeleton height={20} width={'85%'} />
                      <Skeleton height={20} width={'60%'} />
                    </FlexColumn>
                  </GridTile>
                ))
            : renderPaginatedData()}
        </Grid>
      )}
      <PageTile>
        <TableSettings
          {...paginationProps}
          hideRowsPerPage
          totalRowsLabel={totalRowsLabel}
        />
      </PageTile>
    </StyledFlexColumn>
  );
};

export default TilesGrid;
